/**
 * Composable untuk semua master data.
 *
 * staleTime: 1 jam (master jarang berubah).
 * cacheTime: 2 jam (default global).
 *
 * Pattern:
 *   const { data, isLoading, error, refetch } = useBranches();
 *
 * Karena Vue 2 + composition-api, ref value diakses via `.value` di setup(),
 * tapi setelah di-return ke template Vue meng-unwrap otomatis.
 *
 * Untuk invalidate (mis. setelah create/update branch):
 *   const qc = useQueryClient();
 *   qc.invalidateQueries({ queryKey: keys.master.branches() });
 *   atau invalidate semua master:
 *   qc.invalidateQueries({ queryKey: keys.master.all });
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchBranches,
  fetchCouriers,
  fetchBanks,
  fetchBankCouriers,
  fetchPayments,
  fetchJobs,
  fetchRoles,
  fetchPickups,
  fetchCostCategories,
  fetchDestinations,
  fetchServices,
  fetchAgens,
  fetchAgenOptions,
  fetchMembers,
  fetchPageSetting,
  fetchCheckOngkirProvinces,
  fetchCheckOngkirCities,
} from "@/queries/api/master";

const MASTER_STALE = 60 * 60 * 1000; // 1 jam

function masterQuery(queryKey, queryFn, options = {}) {
  return useQuery({
    queryKey,
    queryFn,
    staleTime: MASTER_STALE,
    ...options,
  });
}

export function useBranches(options = {}) {
  return masterQuery(keys.master.branches(), fetchBranches, options);
}

export function useCouriers(options = {}) {
  return masterQuery(keys.master.couriers(), fetchCouriers, options);
}

export function useBanks(options = {}) {
  return masterQuery(keys.master.banks(), fetchBanks, options);
}

export function useBankCouriers(options = {}) {
  return masterQuery(keys.master.bankCouriers(), fetchBankCouriers, options);
}

export function usePayments(options = {}) {
  return masterQuery(keys.master.payments(), fetchPayments, options);
}

export function useJobs(options = {}) {
  return masterQuery(keys.master.jobs(), fetchJobs, options);
}

export function useRoles(options = {}) {
  return masterQuery(keys.master.roles(), fetchRoles, options);
}

export function usePickups(options = {}) {
  return masterQuery(keys.master.pickups(), fetchPickups, options);
}

export function useCostCategories(options = {}) {
  return masterQuery(
    keys.master.costCategories(),
    fetchCostCategories,
    options
  );
}

export function useDestinations(options = {}) {
  return masterQuery(keys.master.destinations(), fetchDestinations, options);
}

export function useServices(options = {}) {
  return masterQuery(keys.master.services(), fetchServices, options);
}

/**
 * Agens — params dapat berupa ref/plain object.
 * Query akan otomatis re-fetch ketika params berubah (TanStack auto-tracking).
 */
export function useAgens(params = {}, options = {}) {
  return useQuery({
    queryKey: keys.master.agens(params),
    queryFn: () => fetchAgens(params),
    staleTime: MASTER_STALE,
    ...options,
  });
}

/**
 * Agen Options — payload minimal (id/fullname/branch.city_name) untuk dropdown filter.
 * Gunakan ini di mana pun kamu hanya butuh daftar agen untuk select/autocomplete.
 */
export function useAgenOptions(params = {}, options = {}) {
  return useQuery({
    queryKey: keys.master.agenOptions(params),
    queryFn: () => fetchAgenOptions(params),
    staleTime: MASTER_STALE,
    ...options,
  });
}

export function useMembers(params = {}, options = {}) {
  return useQuery({
    queryKey: keys.master.members(params),
    queryFn: () => fetchMembers(params),
    staleTime: MASTER_STALE,
    ...options,
  });
}

/**
 * Page Setting — konfigurasi home/about/faq/contact (single record).
 * staleTime ikut MASTER_STALE (1 jam) karena jarang berubah.
 */
export function usePageSetting(options = {}) {
  return useQuery({
    queryKey: keys.master.pageSetting(),
    queryFn: fetchPageSetting,
    staleTime: MASTER_STALE,
    ...options,
  });
}

/**
 * Check Ongkir Provinces — data RajaOngkir, relatif statis. Reuse MASTER_STALE.
 */
export function useCheckOngkirProvinces(options = {}) {
  return useQuery({
    queryKey: keys.master.checkOngkirProvinces(),
    queryFn: fetchCheckOngkirProvinces,
    staleTime: MASTER_STALE,
    ...options,
  });
}

/**
 * Check Ongkir Cities — by province_id. Param boleh ref/computed/plain.
 * Otomatis disabled saat province falsy supaya tidak hit endpoint kosong.
 */
export function useCheckOngkirCities(province, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.master.checkOngkirCities(unref(province))),
    queryFn: () => fetchCheckOngkirCities(unref(province)),
    staleTime: MASTER_STALE,
    enabled: computed(() => !!unref(province)),
    ...options,
  });
}
